import { api, unwrap } from '@/api'
import type { CarBrand, CarModel, CarGeneration, CarVariant, CarFeature, CarListing } from '@/modules/cars/services/cars.service'

export interface AdminCarsOverviewStats {
  totalBrands: number
  totalModels: number
  totalGenerations: number
  totalVariants: number
  totalFeatures: number
  totalListings: number
  pendingListings: number
  publishedListings: number
  pendingModelRequests: number
  recentListings: CarListing[]
}

export interface ModelRequestItem {
  id: number
  userId: number
  brandName: string
  modelName: string
  variantName?: string
  year?: number
  notes?: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  createdAt: string
  user?: {
    id: number
    firstName: string
    lastName: string
    email: string
  }
}

export const carsAdminService = {
  // Overview
  async getOverviewStats(): Promise<AdminCarsOverviewStats> {
    const res = await api.get('/platform/admin/cars/overview')
    return unwrap(res) as AdminCarsOverviewStats
  },

  // Brands
  async getBrands(_includeInactive = true): Promise<CarBrand[]> {
    const res = await api.get('/cars/brands', { params: { includeInactive: true } })
    return unwrap(res) as CarBrand[]
  },

  async createBrand(payload: { name: string; logoUrl?: string; isFeatured?: boolean; displayOrder?: number }): Promise<CarBrand> {
    const res = await api.post('/platform/admin/cars/brands', payload)
    return unwrap(res) as CarBrand
  },

  // Models
  async getModelsByBrand(brandId: number): Promise<CarModel[]> {
    const res = await api.get(`/cars/brands/${brandId}/models`)
    return unwrap(res) as CarModel[]
  },

  async createModel(payload: { brandId: number; name: string; bodyType?: string; displayOrder?: number }): Promise<CarModel> {
    const res = await api.post('/platform/admin/cars/models', payload)
    return unwrap(res) as CarModel
  },

  // Generations
  async getGenerationsByModel(modelId: number): Promise<CarGeneration[]> {
    const res = await api.get(`/cars/models/${modelId}/generations`)
    return unwrap(res) as CarGeneration[]
  },

  async createGeneration(payload: { modelId: number; name: string; startYear: number; endYear?: number }): Promise<CarGeneration> {
    const res = await api.post('/platform/admin/cars/generations', payload)
    return unwrap(res) as CarGeneration
  },

  // Variants
  async getVariantsByGeneration(generationId: number): Promise<CarVariant[]> {
    const res = await api.get(`/cars/generations/${generationId}/variants`)
    return unwrap(res) as CarVariant[]
  },

  async createVariant(payload: {
    generationId: number
    name: string
    fuelType: string
    transmission: string
    engineCc?: number
    horsepower?: number
    torqueNm?: number
    mileageKmpl?: number
    seatingCapacity?: number
  }): Promise<CarVariant> {
    const res = await api.post('/platform/admin/cars/variants', payload)
    return unwrap(res) as CarVariant
  },

  // Features
  async getFeatures(): Promise<CarFeature[]> {
    const res = await api.get('/cars/features')
    return unwrap(res) as CarFeature[]
  },

  async createFeature(payload: { category: string; name: string; code: string; description?: string }): Promise<CarFeature> {
    const res = await api.post('/platform/admin/cars/features', payload)
    return unwrap(res) as CarFeature
  },

  // Listings / Moderation
  async getAdminListings(status?: string): Promise<CarListing[]> {
    const res = await api.get('/platform/admin/cars/listings', { params: { status } })
    return unwrap(res) as CarListing[]
  },

  async approveListing(id: number): Promise<CarListing> {
    const res = await api.post(`/platform/admin/cars/listings/${id}/approve`)
    return unwrap(res) as CarListing
  },

  async rejectListing(id: number, reason: string): Promise<CarListing> {
    const res = await api.post(`/platform/admin/cars/listings/${id}/reject`, { reason })
    return unwrap(res) as CarListing
  },

  // Model Requests
  async getModelRequests(): Promise<ModelRequestItem[]> {
    const res = await api.get('/platform/admin/cars/model-requests')
    return unwrap(res) as ModelRequestItem[]
  },

  async updateModelRequestStatus(id: number, status: 'APPROVED' | 'REJECTED'): Promise<ModelRequestItem> {
    const res = await api.post(`/platform/admin/cars/model-requests/${id}/status`, { status })
    return unwrap(res) as ModelRequestItem
  },

  // Delete methods
  async deleteBrand(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/brands/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },

  async deleteModel(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/models/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },

  async deleteGeneration(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/generations/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },

  async deleteVariant(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/variants/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },

  async deleteFeature(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/features/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },

  async deleteListing(id: number): Promise<{ success: boolean; message: string }> {
    const res = await api.delete(`/platform/admin/cars/listings/${id}`)
    return unwrap(res) as { success: boolean; message: string }
  },
}
